/**
 * Ticket Lifecycle v2 — Job-Mix Classifier (Slice 5A.5)
 *
 * Pure helper that buckets a ticket's job mix into one of five categories
 * so future Advisor-Delay-Impact math can weight advisor-side time
 * differently depending on the type of work the ticket carries.
 *
 * Buckets and intended advisor lifecycle weight:
 *
 *   bucket               | label                              | weight
 *   ---------------------|------------------------------------|-------
 *   diag_only            | Inspection/Diag Only               | 1.0
 *   diag_plus_work       | Inspection/Diag + Work             | null (overlap-adjusted)
 *   work_only            | Work Only                          | 0.0
 *   work_with_dependency | Work With Hold/Approval Needed     | deferred — never auto-returned
 *   comeback_recheck     | Comeback/Recheck                   | 0.6 (medium; placeholder)
 *
 * Classification rules:
 *   1. If the ticket is flagged comeback/recheck → `comeback_recheck` wins
 *      (overrides job mix).
 *   2. Else if every job is diagnostic/inspection and at least one job
 *      exists → `diag_only`.
 *   3. Else if at least one diag/inspection job AND at least one
 *      non-diag job → `diag_plus_work`. Weight is `null` because impact
 *      is overlap-adjusted at impact-calculation time using the formula
 *        max(0, advisor_observed_minutes − remaining_approved_work_minutes)
 *   4. Else if jobs exist and none are diagnostic/inspection → `work_only`.
 *   5. If no jobs exist → safe default `work_only` with a documented note.
 *
 * `work_with_dependency` (parts hold, approval hold, customer hold, etc.)
 * cannot be classified from jobs alone today. The data model lacks a
 * first-class hold/dependency signal: `tickets.pause_reason` is free-text
 * and `advisor_action_events` parts/approval kinds are dormant (slice 4F
 * flag default OFF). The bucket exists in the union for forward
 * compatibility but is never auto-returned. A future slice with a
 * reliable dependency event source will widen this helper.
 *
 * Doctrinal anchors:
 *   - docs/lifecycle/OPTITIME_LIFECYCLE_BUCKETS_PLAN.md §4 (feasibility
 *     audit), §5 (advisor-weight table).
 *   - `src/utils/diagnosticJobDetection.ts` →
 *     `isDiagnosticOrInspectionJob` is the single source of truth for
 *     per-job diag classification. Re-used here to avoid duplicating
 *     keyword logic.
 *
 * Boundary: this module is pure and analytics-only. It must not be
 * imported into Chronomancer / scheduling / timer / parking / rollover
 * hot paths (see drift detector boundary guard in
 * `tests/unit/server/custodyEnumDriftDetector.test.ts` §9).
 */

import { isDiagnosticOrInspectionJob } from '@/utils/diagnosticJobDetection';

export type TicketJobMixBucket =
  | 'diag_only'
  | 'diag_plus_work'
  | 'work_only'
  | 'work_with_dependency'
  | 'comeback_recheck';

export type TicketJobMixLabel =
  | 'Inspection/Diag Only'
  | 'Inspection/Diag + Work'
  | 'Work Only'
  | 'Work With Hold/Approval Needed'
  | 'Comeback/Recheck';

export interface JobMixClassification {
  bucket: TicketJobMixBucket;
  label: TicketJobMixLabel;
  diagnosticJobCount: number;
  workJobCount: number;
  totalJobCount: number;
  /**
   * Advisor lifecycle-time weight. `null` means "overlap-adjusted at
   * impact-calculation time" (currently only `diag_plus_work`).
   * `work_with_dependency` is never auto-returned today, so consumers
   * should not rely on its weight value via this helper.
   */
  advisorWeight: number | null;
  /**
   * True only when a reliable runtime dependency/hold signal is
   * available. Today no such signal exists, so this is always `false`.
   * Reserved for a future slice.
   */
  dependencyDriven: boolean;
  /** Optional human-readable notes (e.g. for empty-jobs default). */
  notes?: string[];
}

/**
 * Minimal ticket shape consumed by the classifier. Accepts either the
 * camelCase `isComeback` (TS `Ticket` interface) or the snake_case
 * `is_comeback` (DB row shape) so the helper is callable from both
 * sides without coercion at the call site.
 */
export interface JobMixTicketLite {
  isComeback?: boolean | null;
  is_comeback?: boolean | null;
}

/**
 * Minimal job shape consumed by the classifier. Mirrors the input
 * accepted by `isDiagnosticOrInspectionJob`. All fields optional —
 * jobs missing every signal classify as non-diagnostic.
 */
export interface JobMixJobLite {
  category?: string | null;
  submenu?: string | null;
  proficiency_category?: string | null;
  name?: string | null;
  label?: string | null;
}

export const ADVISOR_WEIGHT_DIAG_ONLY = 1.0;
export const ADVISOR_WEIGHT_WORK_ONLY = 0.0;
export const ADVISOR_WEIGHT_COMEBACK = 0.6;

const LABELS: Record<TicketJobMixBucket, TicketJobMixLabel> = {
  diag_only: 'Inspection/Diag Only',
  diag_plus_work: 'Inspection/Diag + Work',
  work_only: 'Work Only',
  work_with_dependency: 'Work With Hold/Approval Needed',
  comeback_recheck: 'Comeback/Recheck',
};

function isComebackTicket(ticket: JobMixTicketLite): boolean {
  return ticket.isComeback === true || ticket.is_comeback === true;
}

/**
 * Classify a ticket's job mix. Pure: same inputs always produce the
 * same output; never mutates the input ticket or jobs array.
 */
export function classifyTicketJobMix(
  ticket: JobMixTicketLite,
  jobs: ReadonlyArray<JobMixJobLite>,
): JobMixClassification {
  const totalJobCount = jobs.length;

  let diagnosticJobCount = 0;
  for (const job of jobs) {
    if (
      isDiagnosticOrInspectionJob({
        category: job.category ?? undefined,
        submenu: job.submenu ?? undefined,
        proficiency_category: job.proficiency_category ?? undefined,
        name: job.name ?? undefined,
        label: job.label ?? undefined,
      })
    ) {
      diagnosticJobCount++;
    }
  }
  const workJobCount = totalJobCount - diagnosticJobCount;

  if (isComebackTicket(ticket)) {
    return {
      bucket: 'comeback_recheck',
      label: LABELS.comeback_recheck,
      diagnosticJobCount,
      workJobCount,
      totalJobCount,
      advisorWeight: ADVISOR_WEIGHT_COMEBACK,
      dependencyDriven: false,
      notes: [
        'Ticket flagged as comeback/recheck; classification overrides job mix.',
      ],
    };
  }

  if (totalJobCount === 0) {
    return {
      bucket: 'work_only',
      label: LABELS.work_only,
      diagnosticJobCount,
      workJobCount,
      totalJobCount,
      advisorWeight: ADVISOR_WEIGHT_WORK_ONLY,
      dependencyDriven: false,
      notes: [
        'No jobs present; defaulted to Work Only until jobs are added.',
      ],
    };
  }

  if (diagnosticJobCount > 0 && workJobCount === 0) {
    return {
      bucket: 'diag_only',
      label: LABELS.diag_only,
      diagnosticJobCount,
      workJobCount,
      totalJobCount,
      advisorWeight: ADVISOR_WEIGHT_DIAG_ONLY,
      dependencyDriven: false,
    };
  }

  if (diagnosticJobCount > 0 && workJobCount > 0) {
    return {
      bucket: 'diag_plus_work',
      label: LABELS.diag_plus_work,
      diagnosticJobCount,
      workJobCount,
      totalJobCount,
      advisorWeight: null,
      dependencyDriven: false,
      notes: [
        'Advisor delay impact is overlap-adjusted: max(0, advisor_observed_minutes − remaining_approved_work_minutes).',
      ],
    };
  }

  return {
    bucket: 'work_only',
    label: LABELS.work_only,
    diagnosticJobCount,
    workJobCount,
    totalJobCount,
    advisorWeight: ADVISOR_WEIGHT_WORK_ONLY,
    dependencyDriven: false,
  };
}
